<script lang="ts">
	import type { PropertyMedia } from "$adapters/webflow/types";
	import { onMount } from "svelte";
	import { ChevronLeft, ChevronRight } from "@lucide/svelte";

	let { media }: { media?: PropertyMedia[] } = $props();

	let currentImageIndex = $state(0);
	let isTransitioning = $state(false);

	$effect(() => {
		if (media?.length) {
			currentImageIndex = 0;
		}
	});

	function nextImage() {
		if (isTransitioning || !media) return;
		isTransitioning = true;
		currentImageIndex = (currentImageIndex + 1) % media!.length;
		setTimeout(() => {
			isTransitioning = false;
		}, 300);
	}

	function prevImage() {
		if (isTransitioning || !media) return;
		isTransitioning = true;
		currentImageIndex = (currentImageIndex - 1 + media!.length) % media!.length;
		setTimeout(() => {
			isTransitioning = false;
		}, 300);
	}

	onMount(() => {
		const handleKeydown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft") prevImage();
			if (e.key === "ArrowRight") nextImage();
		};
		window.addEventListener("keydown", handleKeydown);
		return () => window.removeEventListener("keydown", handleKeydown);
	});
</script>

<section
	id="image-gallery"
	class="relative mb-4 h-[400px] w-full overflow-hidden rounded lg:h-[600px]"
>
	{#await media}
		Loading Media
	{:then}
		{#if !media?.length}
			<div class="bg-accent flex h-full w-full items-center justify-center">
				<span class="text-muted-foreground text-lg">No images available</span>
			</div>
		{:else}
			<!-- Main Image -->
			<div class="relative h-full w-full">
				<img
					src={media[currentImageIndex].url}
					alt={`Property image ${currentImageIndex + 1}`}
					class="h-full w-full object-cover transition-opacity duration-300 {isTransitioning
						? 'opacity-80'
						: ''}"
				/>
				<!-- Navigation Arrows -->
				<button
					class="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-opacity hover:bg-black/70"
					onclick={prevImage}
				>
					<ChevronLeft class="h-6 w-6" />
				</button>
				<button
					class="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-opacity hover:bg-black/70"
					onclick={nextImage}
				>
					<ChevronRight class="h-6 w-6" />
				</button>
				<!-- Image Counter -->
				<div
					class="absolute bottom-4 right-4 rounded bg-black/50 px-3 py-1 text-sm text-white"
				>
					{currentImageIndex + 1} / {media.length}
				</div>
			</div>
			<!-- Thumbnail Strip -->
			{#if media.length > 1}
				<div
					class="absolute bottom-0 left-0 flex w-full gap-2 overflow-x-auto bg-black/50 p-2"
				>
					{#each media as image, index}
						<button
							class="h-16 w-24 flex-shrink-0 overflow-hidden rounded {currentImageIndex ===
							index
								? 'ring-2 ring-white'
								: ''}"
							onclick={() => {
								currentImageIndex = index;
							}}
						>
							<img
								src={image.url}
								alt={`Thumbnail ${index + 1}`}
								class="h-full w-full object-cover"
							/>
						</button>
					{/each}
				</div>
			{/if}
		{/if}
	{/await}
</section>
